/**
 * 操控变形提交：把图钉位移一次性烘进图层像素（Puppet Warp 计划）。
 *
 * 结构复用液化的 bake 骨架（`engine/liquify/bake.ts` 的
 * `flattenLayerRegion`/`writeWarpedRegionToTiles`）——两者的差异只在
 * "怎么算出 dx/dy"，展平源区域/写回这两步完全一致。
 */
import { TILE, type LayerId } from '../tile/key';
import type { TileStore } from '../tile/store';
import type { StrokeTransaction } from '../brush/buffer';
import { flattenLayerRegion, writeWarpedRegionToTiles } from '../liquify/bake';
import { warpRegion } from '../liquify/warp';
import type { Rect } from '../liquify/field';
import { computeMlsField, type MlsPin } from './mls';

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/**
 * 图层内容的外接矩形（扫描 alpha，同 `transform-tool.ts` 的 `#contentBounds`
 * 思路，但那是交互层私有方法，跨层引用不合规，这里在引擎层独立写一份）。
 * 全透明/无像素时返回 0 宽高的矩形。
 */
export function contentBounds(store: TileStore, layer: LayerId, docWidth: number, docHeight: number): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const tile of store.residentTilesOf(layer, 0)) {
    const data = tile.data;
    if (data === null) continue;
    const originX = tile.key.tx * TILE;
    const originY = tile.key.ty * TILE;
    for (let ly = 0; ly < TILE; ly++) {
      const rowBase = ly * TILE * 4;
      for (let lx = 0; lx < TILE; lx++) {
        if (data[rowBase + lx * 4 + 3] === 0) continue;
        const x = originX + lx;
        const y = originY + ly;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x + 1 > maxX) maxX = x + 1;
        if (y + 1 > maxY) maxY = y + 1;
      }
    }
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return {
    x: Math.max(0, minX),
    y: Math.max(0, minY),
    width: Math.min(docWidth, maxX) - Math.max(0, minX),
    height: Math.min(docHeight, maxY) - Math.max(0, minY),
  };
}

export interface PuppetBakeResult {
  readonly bounds: Rect;
  readonly dirty: ReadonlyArray<{ tx: number; ty: number }>;
}

export interface PuppetBakeOptions {
  readonly signal?: AbortSignal;
}

/**
 * 把 `pins` 定义的变形一次性烘进 `layer` 的像素。没有任何图钉被拖动过
 * （`current` 都等于各自 `origin`）返回 `null`，不触碰 TileStore、不声明
 * 任何事务块。
 */
export async function bakePuppetWarp(
  store: TileStore,
  layer: LayerId,
  pins: readonly MlsPin[],
  docWidth: number,
  docHeight: number,
  transaction: StrokeTransaction,
  options: PuppetBakeOptions = {},
): Promise<PuppetBakeResult | null> {
  const moved = pins.filter((pin) => pin.current.x !== pin.origin.x || pin.current.y !== pin.origin.y);
  if (moved.length === 0) return null;

  const bounds = contentBounds(store, layer, docWidth, docHeight);
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  throwIfAborted(options.signal);

  const source = flattenLayerRegion(store, layer, bounds);
  throwIfAborted(options.signal);

  const { dx, dy } = await computeMlsField(moved, bounds, options);
  const output = await warpRegion(source, bounds.width, bounds.height, dx, dy, options);
  throwIfAborted(options.signal);

  const { dirty } = writeWarpedRegionToTiles(store, layer, bounds, output, transaction);
  return { bounds, dirty };
}
